import React, { useEffect, useMemo, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import ContainerHead from 'src/components/ui/ContainerOverlay/ContainerHead/ContainerHead';
import Icon from 'src/components/ui/Icon/Icon';
import { extractBackendErrorMessage } from 'src/pages/Register/errorUtils';
import { useUserMutations } from 'src/services/user';
import logger from 'src/utils/logger';
import WizardStepBar from './WizardStepBar';
import UserWizardCredentialsStep from './steps/UserWizardCredentialsStep';
import UserWizardProfileStep from './steps/UserWizardProfileStep';
import { DEFAULT_PERMISSION_LEVELS, PERMISSION_AREA_KEYS, permissionLevelsToRoles } from './userWizardLogic';
import {
  CredentialsStepValues,
  ProfileStepValues,
  createCredentialsStepSchema,
  createProfileStepSchema,
} from './userWizardSchema';

type Props = {
  /** Close attempt from the header X, the footer "cancel" or Escape. */
  onRequestClose: () => void;
  /** Called only after the user was created successfully. */
  onCreated: () => void;
};

const TOTAL_STEPS = 2;

/**
 * Two-step "add user" flow.
 *
 * The point of the rebuild: the user is created with a *single* POST that
 * carries the initial password, so a failure can no longer leave behind a user
 * without a password. On failure the overlay stays open, the inputs survive and
 * the backend's own message is shown instead of a generic toast.
 */
const UserCreateWizard: React.FC<Props> = ({ onRequestClose, onCreated }) => {
  const { t } = useTranslation();
  const { createMutation } = useUserMutations();

  const [step, setStep] = useState(1);
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const profileSchema = useMemo(() => createProfileStepSchema(t), [t]);
  const credentialsSchema = useMemo(() => createCredentialsStepSchema(t), [t]);

  const profileForm = useForm<ProfileStepValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: { name: '', permissions: DEFAULT_PERMISSION_LEVELS },
    mode: 'onChange',
  });

  const credentialsForm = useForm<CredentialsStepValues>({
    resolver: zodResolver(credentialsSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onChange',
  });

  const stepLabels = [t('general.user_wizard.step_profile'), t('general.user_wizard.step_credentials')];

  // A stale error banner must not follow the user around: it goes away as soon
  // as they change a step or start correcting the input it complained about.
  const clearSubmitError = () => setSubmitError(previous => (previous === null ? previous : null));

  const goToStep = (target: number) => {
    if (target < 1 || target > maxVisitedStep) return;
    clearSubmitError();
    setStep(target);
  };

  const goToNextStep = profileForm.handleSubmit(() => {
    clearSubmitError();
    setMaxVisitedStep(visited => Math.max(visited, 2));
    setStep(2);
  });

  useEffect(() => {
    const subscription = credentialsForm.watch(() => clearSubmitError());
    return () => subscription.unsubscribe();
  }, [credentialsForm]);

  const submit = credentialsForm.handleSubmit(async credentials => {
    const profile = profileForm.getValues();
    setSubmitError(null);

    try {
      await createMutation.mutateAsync({
        body: {
          name: profile.name.trim(),
          email: credentials.email.trim(),
          login: null,
          // No language picker in the wizard, so let the backend decide: its
          // _provisioning_values does `user.lang or self.env.user.lang`, i.e. the
          // new user inherits the language of whoever created them. Sending a
          // hard en_US here would hand every colleague of a German merchant an
          // English portal.
          lang: null,
          roles: permissionLevelsToRoles(t, profile.permissions),
          notification_email: false,
          notification_sms: false,
          notification_daily_updates: false,
          is_in_dark_mode: false,
          // Same request, same transaction — no follow-up set_initial_password call.
          password: credentials.password,
        },
      });

      toast.success(t('success.user_created_successfully'));
      onCreated();
    } catch (error) {
      logger.error('Failed to create user', error);
      setSubmitError(extractBackendErrorMessage(error) || t('errors.error_creating_user'));
    }
  });

  useEffect(() => {
    if (submitError) errorRef.current?.focus();
  }, [submitError]);

  const grantFullAccessToAll = () => {
    PERMISSION_AREA_KEYS.forEach(area => {
      profileForm.setValue(`permissions.${area}`, 'read_write', { shouldDirty: true, shouldValidate: true });
    });
  };

  const isFirstStep = step === 1;
  const isPrimaryDisabled = isFirstStep
    ? !profileForm.formState.isValid
    : !credentialsForm.formState.isValid || createMutation.isPending;

  return (
    <>
      <ContainerHead>
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <h2 className="typo-text-16--bold text-full truncate">{t('general.add_user')}</h2>
          <p className="typo-text-12 text-faded truncate">
            {t('general.user_wizard.step_indicator', {
              current: step,
              total: TOTAL_STEPS,
              name: stepLabels[step - 1],
            })}
          </p>
        </div>
        <Button
          icon="close"
          ariaLabel={t('general.close')}
          tone={ButtonToneProps.SECONDARY}
          onClick={onRequestClose}
          className="flex-shrink-0"
        />
      </ContainerHead>

      <WizardStepBar
        stepLabels={stepLabels}
        currentStep={step}
        maxVisitedStep={maxVisitedStep}
        onStepSelect={goToStep}
        ariaLabel={t('general.add_user')}
      />

      <ContainerBody className="flex min-h-0 flex-1 flex-col gap-24">
        {submitError && (
          <div
            ref={errorRef}
            tabIndex={-1}
            role="alert"
            className="status-error-surface status-error-full rounded-base flex items-start gap-8 p-12">
            <Icon name="error" width={16} height={16} />
            <p className="typo-text-12--bold">{submitError}</p>
          </div>
        )}

        {isFirstStep ? (
          <UserWizardProfileStep
            control={profileForm.control}
            onGrantFullAccessToAll={grantFullAccessToAll}
            onSubmit={goToNextStep}
          />
        ) : (
          <UserWizardCredentialsStep
            control={credentialsForm.control}
            name={profileForm.watch('name')}
            permissions={profileForm.watch('permissions')}
            password={credentialsForm.watch('password')}
            onEditProfile={() => goToStep(1)}
            onSubmit={submit}
          />
        )}
      </ContainerBody>

      <div className="surface-elevated border-subtle-top flex flex-shrink-0 items-center gap-12 px-32 py-16">
        {!isFirstStep && (
          <Button tone={ButtonToneProps.SECONDARY} title={t('general.back')} onClick={() => goToStep(step - 1)} />
        )}
        <div className="flex-1" />
        <Button tone={ButtonToneProps.GHOST} title={t('general.cancel')} onClick={onRequestClose} />
        <Button
          tone={ButtonToneProps.PRIMARY_BLUE}
          title={isFirstStep ? t('general.next') : t('general.add_user')}
          onClick={isFirstStep ? goToNextStep : submit}
          disabled={isPrimaryDisabled}
        />
      </div>
    </>
  );
};

export default UserCreateWizard;
